import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Sparkles } from "lucide-react";
import Reveal from "./Reveal";
import { featuredCaseStudy } from "../data/site";

/**
 * Featured Case Study — a single creative spotlight (the printing brochure).
 * Distinct background: deep velvet navy with gold sheen + paper-grain overlay,
 * meant to stand out clearly from regular sections on the home page.
 */
export default function FeaturedCaseStudy() {
  const f = featuredCaseStudy;

  return (
    <section
      className="relative overflow-hidden py-20 md:py-28"
      data-testid="featured-case-study"
      id="featured-case-study"
      style={{
        background:
          "radial-gradient(circle at 18% 0%, rgba(245,200,66,0.22), transparent 55%), radial-gradient(circle at 100% 100%, rgba(245,200,66,0.10), transparent 50%), linear-gradient(180deg, #160F1F 0%, #0E0A1A 100%)",
      }}
    >
      {/* paper-grain texture overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 0.85  0 0 0 0 0.55  0 0 0 1 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* gold accent line top + bottom */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/55 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* TEXT */}
          <Reveal className="lg:col-span-6 lg:order-1 order-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.28em] text-gold font-semibold">
              <Sparkles className="h-3 w-3" />
              {f.kicker}
            </span>
            <h2 className="h-display text-4xl sm:text-5xl md:text-6xl mt-6 text-white">
              {f.label}
            </h2>
            <p className="mt-5 text-xl md:text-2xl text-gold/90 font-heading font-medium tracking-tight max-w-xl">
              {f.case_study.tagline}
            </p>
            <p className="mt-6 text-base sm:text-lg text-[#D7D3C7] leading-relaxed max-w-2xl">
              {f.case_study.short}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to={`/case-studies/${f.slug}`}
                data-testid="featured-case-cta"
                className="glow-btn inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm md:text-base font-semibold text-[#0A0F2C] hover:brightness-105"
              >
                <BookOpen className="h-4 w-4" />
                Read the full case study
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services/printing-publishing"
                data-testid="featured-case-service"
                className="text-sm font-semibold text-[#D7D3C7] hover:text-white transition-colors inline-flex items-center gap-1.5"
              >
                Explore Printing &amp; Publishing
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          {/* IMAGE */}
          <Reveal className="lg:col-span-6 lg:order-2 order-1" delay={120}>
            <div className="relative">
              {/* gold framed card */}
              <div className="relative rounded-[28px] overflow-hidden border border-gold/30 shadow-[0_30px_70px_-20px_rgba(245,200,66,0.35),_0_0_40px_rgba(245,200,66,0.15)]">
                <div className="aspect-[4/3] relative">
                  <img
                    src={f.image}
                    alt="Featured case study — Burial Tribute & Photo Gallery Brochure"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0F2C]/35 via-transparent to-gold/15" />
                </div>
                {/* tag pill */}
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-[#0A0F2C]/85 backdrop-blur border border-white/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">
                  Print · Editorial
                </div>
              </div>
              {/* floating credit card */}
              <div className="hidden md:flex items-center gap-3 absolute -bottom-6 -left-6 rounded-2xl bg-[#0A0F2C]/85 backdrop-blur border border-white/10 px-5 py-3 shadow-xl">
                <div className="h-9 w-9 rounded-lg bg-gold/15 grid place-items-center text-gold">
                  <BookOpen className="h-4 w-4" />
                </div>
                <div className="leading-tight">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#CBD5E1]">
                    Tribute Brochure
                  </p>
                  <p className="text-sm font-heading font-semibold text-white">
                    Heavyweight silk · Bespoke layout
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
